/**
 * Per-account fee-invoice lifecycle client (#17, fee-invoice slice).
 *
 * Backend router: prefix /agreement-fee-invoices (Admin-only).
 *   - GET    /history/{agreement_id}        per-account billing history + invoice cross-check
 *   - POST   /                  (multipart)  upload an invoice (frozen → issued)
 *   - POST   /{invoice_id}/settle            mark issued → settled
 *   - POST   /{invoice_id}/unsettle          mark settled → issued
 *   - PATCH  /{invoice_id}      (multipart)  amend amounts and/or replace the PDF
 *   - DELETE /{invoice_id}                   soft-delete (= cancel), 204
 *   - GET    /{invoice_id}/pdf               stream the stored PDF (binary attachment)
 *
 * Money fields are integer euro-cents; convert at the form boundary with the
 * helpers in ./feeSchedules (eurToCents / centsToEur / fmtEurFromCents). The
 * multipart create/amend + the PDF download use a raw same-origin fetch (the
 * JSON `api` client only sends/receives JSON), mirroring monthlyMetrics.ts
 * (FormData import) and feeRuns.ts (downloadBlob + Content-Disposition).
 */
import { api } from './client';
import { downloadBlob } from './iaaExports';
import type { DataSpanResponse } from './reports';
import type { ISODate, ISODateTime, UUID } from './types';

export type FeeInvoiceStatus = 'frozen' | 'issued' | 'settled';

/** One per-account billing row for a month, with the invoice cross-check.
 *  `frozen` = a computed run with no invoice yet (diffs/mismatch null/false);
 *  `issued` = a PDF has been uploaded; `settled` = the invoice is settled. */
export interface FeeHistoryRow {
  run_id: UUID;
  agreement_id: UUID;
  agreement_code: string | null;
  agreement_mnemonic: string | null;
  account_id: UUID;
  policy_id: UUID | null;
  is_direct: boolean;
  insurer: string | null;
  policy_number: string | null;
  bank_name: string | null;
  account_number: string | null;
  billing_name: string | null;
  period_month: ISODate;          // YYYY-MM-DD (first-of-month)
  aum_eur_cents: number | null;
  effective_rate_bps: number | null;
  net_fee_cents: number;
  vat_cents: number;
  total_fee_cents: number;
  status: FeeInvoiceStatus;
  invoice_id: UUID | null;
  invoiced_net_cents: number | null;
  invoiced_total_cents: number | null;
  net_diff_cents: number | null;
  total_diff_cents: number | null;
  net_mismatch: boolean | null;
  total_mismatch: boolean | null;
  settled_at: ISODateTime | null;
}

/** One HISTORICAL (<= Dec-2025) uploaded fee actual (agreement-grain). No per-account
 *  breakdown exists for these months, so the View-History row is not expandable
 *  (F4, 2026-07-04). `fee_ex_vat_cents` is the net actual; VAT/total are not carried. */
export interface UploadedFeeMonth {
  period_month: ISODate;          // YYYY-MM-DD (first-of-month)
  aum_eur_cents: number | null;
  fee_ex_vat_cents: number;
}

/** The created/updated invoice row (POST / PATCH / settle / unsettle response). */
export interface FeeInvoice {
  id: UUID;
  agreement_id: UUID;
  account_id: UUID;
  period_month: ISODate;
  invoiced_net_cents: number;
  invoiced_total_cents: number;
  status: FeeInvoiceStatus;
  settled_at: ISODateTime | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  created_by: UUID | null;
  updated_by: UUID | null;
}

/** Create-an-invoice input (multipart). `period_month` is the 'YYYY-MM' string. */
export interface FeeInvoiceCreateInput {
  agreement_id: UUID;
  account_id: UUID;
  period_month: string;            // 'YYYY-MM'
  invoiced_net_cents: number;
  invoiced_total_cents: number;
  file: File;
}

/** Amend input (multipart) — all fields optional; omit `file` to keep the PDF. */
export interface FeeInvoiceAmendInput {
  invoiced_net_cents?: number;
  invoiced_total_cents?: number;
  file?: File | null;
}

/** One accountant-Excel FA row in the bulk-import preview (Inc 5e-3). */
export interface InvoiceImportRow {
  row_index: number;
  reference: string | null;
  invoice_number: string | null;
  period_month: ISODate | null;
  agreement_code: string | null;
  account_label: string | null;
  method: string;            // indirect_fund|direct_mnemonic|single_account|value_inferred|no_account|no_agreement
  matched: boolean;
  pdf_found: boolean;
  invoiced_net_cents: number | null;
  invoiced_total_cents: number | null;
  computed_net_cents: number | null;
  computed_total_cents: number | null;
  net_mismatch: boolean;
  total_mismatch: boolean;
  importable: boolean;          // would be CREATED on commit (no existing invoice)
  will_overwrite: boolean;      // overwrite mode: an existing ISSUED invoice would be amended (#17)
  existing: boolean;            // an alive invoice already exists for this account-month (#17)
  pdf_amount_cents: number | null;   // total-incl-VAT parsed from the PDF (#17 gap 3; null = not read)
  pdf_amount_mismatch: boolean;      // |PDF total − Excel total| > €1 — flag only, never blocks
  note: string | null;
}

/** The View-History 'Total' field over a month window (computed/frozen fees).
 *  agreement_id set = scoped to that agreement; null = firm-wide. */
export interface PeriodTotal {
  period_from: ISODate;
  period_to: ISODate;
  agreement_id: UUID | null;
  months: number;            // distinct billed months in the window
  runs: number;              // per-account rows summed
  sum_net_cents: number;
  sum_vat_cents: number;
  sum_total_cents: number;
}

/** Invoice coverage for one billing month touched by the import (#17 reconciliation gap 1). */
export interface PeriodCoverage {
  period_month: ISODate;
  billed: number;        // frozen per-account runs for the month (firm-wide)
  invoiced: number;      // of those, carrying an alive invoice
  not_invoiced: number;  // billed − invoiced (still uncovered)
}

/** Result of a bulk-invoice import (preview or commit). */
export interface InvoiceImportReport {
  mode: string;              // preview | commit
  fa_rows: number;
  nc_skipped: number;
  matched: number;
  unmatched: number;
  pdf_missing: number;
  mismatches: number;
  pdf_mismatches: number;      // rows whose PDF-parsed total ≠ Excel total (#17 gap 3; flag only)
  importable: number;
  created: number;
  overwritable: number;        // overwrite mode: existing ISSUED invoices that would be amended (#17)
  overwritten: number;         // overwrite commit: existing invoices amended
  skipped_existing: number;
  coverage: PeriodCoverage[];  // per billing-month billed/invoiced/not-invoiced (#17 gap 1)
  rows: InvoiceImportRow[];
  errors: string[];
}

/** Pull the file name from a Content-Disposition header (RFC 5987 first). */
function fileNameFromCD(cd: string, fallback: string): string {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}

/** Same error-detail extraction shape as feeRuns.ts / monthlyMetrics.ts. */
async function detailOf(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON body */ }
  return typeof detail === 'string' ? detail : fallback;
}

export const feeInvoicesApi = {
  /** Per-account billing history + invoice cross-check for one agreement. */
  listHistory: (agreementId: UUID): Promise<FeeHistoryRow[]> =>
    api.get<FeeHistoryRow[]>(`/agreement-fee-invoices/history/${agreementId}`),

  /** Firm-wide Per-Month history: every agreement's rows for one month ('YYYY-MM'). */
  listMonthHistory: (period: string): Promise<FeeHistoryRow[]> =>
    api.get<FeeHistoryRow[]>(`/agreement-fee-invoices/history-by-month?period=${period}`),

  /** Historical (<= Dec-2025) uploaded fee actuals for one agreement (agreement-grain),
   *  newest month first — folded into the Per-Agreement View-History as non-expandable
   *  'Uploaded' rows (F4, 2026-07-04). */
  listUploadedHistory: (agreementId: UUID): Promise<UploadedFeeMonth[]> =>
    api.get<UploadedFeeMonth[]>(`/agreement-fee-invoices/uploaded-history/${agreementId}`),

  /** Σ frozen fees over the inclusive month window [periodFrom, periodTo] (both
   *  'YYYY-MM'). `agreementId` set → that agreement only; omitted/null → firm-wide. */
  periodTotal: (periodFrom: string, periodTo: string, agreementId?: UUID | null): Promise<PeriodTotal> => {
    const q = new URLSearchParams({ period_from: periodFrom, period_to: periodTo });
    if (agreementId) q.set('agreement_id', agreementId);
    return api.get<PeriodTotal>(`/agreement-fee-invoices/period-total?${q.toString()}`);
  },

  /** First/last month with FEE data — anchors the View-History period control
   *  (Max / data-horizon). `agreementId` set → that agreement's span (read-open);
   *  omitted/null → firm-wide (Admin only). */
  dataSpan: (agreementId?: UUID | null): Promise<DataSpanResponse> => {
    const q = agreementId ? `?agreement_id=${agreementId}` : '';
    return api.get<DataSpanResponse>(`/agreement-fee-invoices/data-span${q}`);
  },

  /** Upload an invoice PDF + the invoiced amounts (frozen → issued). 201. */
  async create(input: FeeInvoiceCreateInput): Promise<FeeInvoice> {
    const form = new FormData();
    form.append('agreement_id', input.agreement_id);
    form.append('account_id', input.account_id);
    form.append('period_month', input.period_month);
    form.append('invoiced_net_cents', String(input.invoiced_net_cents));
    form.append('invoiced_total_cents', String(input.invoiced_total_cents));
    form.append('file', input.file);
    const res = await fetch('/agreement-fee-invoices', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) throw new Error(await detailOf(res, 'The invoice could not be uploaded.'));
    return res.json();
  },

  /** Mark an issued invoice as settled. */
  settle: (invoiceId: UUID): Promise<FeeInvoice> =>
    api.post<FeeInvoice>(`/agreement-fee-invoices/${invoiceId}/settle`),

  /** Re-open a settled invoice (settled → issued). */
  unsettle: (invoiceId: UUID): Promise<FeeInvoice> =>
    api.post<FeeInvoice>(`/agreement-fee-invoices/${invoiceId}/unsettle`),

  /** Amend the invoiced amounts and/or replace the PDF. Omit fields to keep them. */
  async amend(invoiceId: UUID, input: FeeInvoiceAmendInput): Promise<FeeInvoice> {
    const form = new FormData();
    if (input.invoiced_net_cents != null) form.append('invoiced_net_cents', String(input.invoiced_net_cents));
    if (input.invoiced_total_cents != null) form.append('invoiced_total_cents', String(input.invoiced_total_cents));
    if (input.file) form.append('file', input.file);
    const res = await fetch(`/agreement-fee-invoices/${invoiceId}`, {
      method: 'PATCH',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) throw new Error(await detailOf(res, 'The invoice could not be amended.'));
    return res.json();
  },

  /** Soft-delete (= cancel) an invoice. 204. */
  cancel: (invoiceId: UUID): Promise<void> =>
    api.delete<void>(`/agreement-fee-invoices/${invoiceId}`),

  /** Download the stored invoice PDF (streams a blob → browser save). */
  async downloadPdf(invoiceId: UUID): Promise<void> {
    const res = await fetch(`/agreement-fee-invoices/${invoiceId}/pdf`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) throw new Error(await detailOf(res, 'The invoice PDF could not be downloaded.'));
    const cd = res.headers.get('Content-Disposition') ?? '';
    downloadBlob(await res.blob(), fileNameFromCD(cd, 'invoice.pdf'));
  },

  /** Download the retained received accountant invoice Excel for a billing month (#17 gap 2).
   *  `period` = YYYY-MM. */
  async downloadReceivedFile(period: string): Promise<void> {
    const res = await fetch(`/agreement-fee-invoices/received-file?period=${period}`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) throw new Error(await detailOf(res, 'The received invoice Excel could not be downloaded.'));
    const cd = res.headers.get('Content-Disposition') ?? '';
    downloadBlob(await res.blob(), fileNameFromCD(cd, 'invoices.xlsx'));
  },

  /** Bulk-import accountant invoices: the EOM Excel + the invoice PDFs.
   *  mode='preview' writes nothing (returns the per-row report); mode='commit'
   *  creates the Issued invoices. `overwrite=true` amends existing ISSUED invoices
   *  instead of skipping them (#17). Stateless — the caller re-sends both for commit. */
  async importInvoices(excel: File, pdfs: File[], mode: 'preview' | 'commit', overwrite = false): Promise<InvoiceImportReport> {
    const form = new FormData();
    form.append('mode', mode);
    form.append('overwrite', String(overwrite));
    form.append('excel', excel);
    for (const f of pdfs) form.append('files', f);
    const res = await fetch('/agreement-fee-invoices/import', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) throw new Error(await detailOf(res, 'The invoice import could not be processed.'));
    return res.json();
  },
};
